import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';

const sections = [
  { id: 'data-collection', title: 'Data Collection' },
  { id: 'data-usage', title: 'How We Use Your Data' },
  { id: 'data-sharing', title: 'Data Sharing' },
  { id: 'cookies', title: 'Cookies Policy' },
  { id: 'security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'contact', title: 'Contact Us' },
];

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState('data-collection');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const sectionEls = sections.map(s => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = 'Privacy Policy - AiShopio Dubai UAE Data Protection';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'AiShopio Dubai privacy policy. Learn how we collect, use and protect your personal data in compliance with UAE Federal Decree-Law No. 45 of 2021 and GDPR principles.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy - AiShopio Dubai',
      description: 'AiShopio Dubai privacy policy and data protection information.',
      url: `${siteUrl}/privacy`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${siteUrl}/privacy` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="privacy"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'privacy');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="privacy"]')?.remove(); };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-8 px-6" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-600">Privacy Policy</span>
          </p>
          <p className="text-gray-500 text-sm">Last updated: January 1, 2024 &bull; Effective: January 1, 2024</p>
          <div className="mt-4 p-4 rounded-2xl bg-orange-50 border border-orange-100">
            <p className="text-sm text-gray-700">
              <i className="ri-shield-check-line text-orange-500 mr-2"></i>
              AiShopio is committed to protecting your privacy in compliance with UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection and GDPR principles.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Mobile TOC pills */}
        <div className="lg:hidden flex flex-wrap gap-2 mb-8">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                activeSection === s.id
                  ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-600'
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>

        <div className="flex gap-8">
          {/* Sticky TOC - desktop only */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24 bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Contents</h3>
              <nav className="space-y-1">
                {sections.map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-200 cursor-pointer ${
                      activeSection === s.id
                        ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold'
                        : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div ref={contentRef} className="flex-1 max-w-4xl space-y-10">
            <section id="data-collection" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">1</span>
                Data Collection
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>AiShopio collects personal information that you voluntarily provide when using our services, including:</p>
                <ul className="space-y-2 ml-4">
                  {[
                    'Contact information: name, email address, phone number (+971 format)',
                    'Delivery address: Emirates, area, building, apartment details',
                    'Payment information: processed securely through encrypted payment gateways',
                    'Order history and preferences',
                    'Device information and IP address for security purposes',
                    'Communication records when you contact our support team',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>We collect this information through our website, WhatsApp communications, and order forms. We only collect data that is necessary for providing our services.</p>
              </div>
            </section>

            <section id="data-usage" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">2</span>
                How We Use Your Data
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>We use your personal data for the following purposes:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-truck-line', title: 'Order Fulfillment', desc: 'Processing and delivering your orders across UAE Emirates' },
                    { icon: 'ri-customer-service-line', title: 'Customer Support', desc: 'Responding to inquiries via WhatsApp, email, and phone' },
                    { icon: 'ri-mail-line', title: 'Communications', desc: 'Order confirmations, delivery updates, and promotional offers (with consent)' },
                    { icon: 'ri-shield-line', title: 'Security', desc: 'Fraud prevention and account security monitoring' },
                    { icon: 'ri-bar-chart-line', title: 'Analytics', desc: 'Improving our website and services based on usage patterns' },
                    { icon: 'ri-file-text-line', title: 'Legal Compliance', desc: 'Meeting UAE regulatory and tax requirements' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-orange-50/50">
                      <i className={`${item.icon} text-orange-500 text-lg flex-shrink-0 mt-0.5`}></i>
                      <div>
                        <p className="font-semibold text-gray-800 text-xs">{item.title}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="data-sharing" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">3</span>
                Data Sharing
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>We do not sell your personal data. We may share your information with:</p>
                <ul className="space-y-3 ml-4">
                  {[
                    { title: 'Delivery Partners', desc: 'UAE-based logistics companies to fulfill your orders' },
                    { title: 'Payment Processors', desc: 'Secure payment gateways for transaction processing' },
                    { title: 'Legal Authorities', desc: 'When required by UAE law or court order' },
                    { title: 'Business Transfers', desc: 'In case of merger or acquisition, with prior notice to you' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-arrow-right-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong className="text-gray-800">{item.title}:</strong> {item.desc}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                  <p className="text-green-700 text-sm font-medium">
                    <i className="ri-shield-check-line mr-2"></i>
                    We never sell, rent, or trade your personal information to third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </section>

            <section id="cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">4</span>
                Cookies Policy
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>We use cookies and similar tracking technologies to enhance your browsing experience:</p>
                <div className="space-y-3">
                  {[
                    { type: 'Essential Cookies', desc: 'Required for the website to function properly (cart, session management)', required: true },
                    { type: 'Analytics Cookies', desc: 'Help us understand how visitors use our website to improve performance', required: false },
                    { type: 'Marketing Cookies', desc: 'Used to show relevant advertisements and track campaign effectiveness', required: false },
                  ].map((cookie, i) => (
                    <div key={i} className="flex items-start justify-between gap-4 p-3 rounded-xl bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-800 text-xs">{cookie.type}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{cookie.desc}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${cookie.required ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                        {cookie.required ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  ))}
                </div>
                <p>You can control cookie settings through your browser preferences. Disabling essential cookies may affect website functionality.</p>
              </div>
            </section>

            <section id="security" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">5</span>
                Data Security
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>We implement industry-standard security measures to protect your personal data:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { icon: 'ri-lock-line', label: '256-bit SSL Encryption' },
                    { icon: 'ri-shield-check-line', label: 'PCI DSS Compliant' },
                    { icon: 'ri-server-line', label: 'Secure Data Centers' },
                    { icon: 'ri-eye-off-line', label: 'Access Controls' },
                  ].map((item, i) => (
                    <div key={i} className="text-center p-3 rounded-xl bg-orange-50">
                      <i className={`${item.icon} text-orange-500 text-2xl mb-2 block`}></i>
                      <p className="text-xs font-semibold text-gray-700">{item.label}</p>
                    </div>
                  ))}
                </div>
                <p>We retain your personal data only as long as necessary to fulfill the purposes outlined in this policy or as required by UAE law. Order data is retained for 5 years for tax compliance purposes.</p>
              </div>
            </section>

            <section id="your-rights" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">6</span>
                Your Rights
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 space-y-4 text-gray-700 text-sm leading-relaxed">
                <p>Under UAE data protection law and GDPR principles, you have the following rights:</p>
                <div className="space-y-3">
                  {[
                    { right: 'Right to Access', desc: 'Request a copy of the personal data we hold about you' },
                    { right: 'Right to Rectification', desc: 'Request correction of inaccurate or incomplete data' },
                    { right: 'Right to Erasure', desc: 'Request deletion of your personal data (subject to legal obligations)' },
                    { right: 'Right to Portability', desc: 'Receive your data in a structured, machine-readable format' },
                    { right: 'Right to Object', desc: 'Object to processing of your data for marketing purposes' },
                    { right: 'Right to Withdraw Consent', desc: 'Withdraw consent for optional data processing at any time' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100">
                      <i className="ri-user-settings-line text-orange-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-gray-800 text-xs">{item.right}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p>To exercise any of these rights, contact us at privacy@aishopio.ae or via WhatsApp at +971 50 000 0000. We will respond within 30 days.</p>
              </div>
            </section>

            <section id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">7</span>
                Contact Us
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed">
                <p className="mb-4">For privacy-related inquiries, contact our Data Protection Officer:</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3"><i className="ri-mail-line text-orange-500"></i><span>privacy@aishopio.ae</span></div>
                  <div className="flex items-center gap-3"><i className="ri-whatsapp-line text-green-500"></i><span>+971 50 000 0000</span></div>
                  <div className="flex items-center gap-3"><i className="ri-map-pin-line text-orange-500"></i><span>Dubai, United Arab Emirates</span></div>
                </div>
                <p className="mt-4 text-gray-500">This Privacy Policy was last updated on January 1, 2024. We reserve the right to update this policy and will notify you of significant changes via email.</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
